import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const SavedSearchModal = ({ isOpen, onClose, filters, onSaveSearch }) => {
  const [searchName, setSearchName] = useState('');
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [alertFrequency, setAlertFrequency] = useState('daily');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!searchName?.trim()) return;
    
    setIsSaving(true);
    
    const searchData = {
      name: searchName,
      filters,
      emailAlerts,
      alertFrequency,
      createdAt: new Date()?.toISOString()
    };
    
    // Simulate API call
    setTimeout(() => {
      onSaveSearch(searchData);
      setIsSaving(false);
      onClose();
      setSearchName('');
      setEmailAlerts(true);
      setAlertFrequency('daily');
    }, 1000);
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters)?.filter(value => {
      if (Array.isArray(value)) return value?.length > 0;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') return value?.length > 0;
      return false;
    })?.length;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1000 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-md"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md bg-card rounded-2xl shadow-elevation-lg animate-fade-in">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Save Search</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Get notified when new candidates match your criteria
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-micro"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Search Summary */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-2">Current Search</h3>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Filter" size={16} />
              <span>{getActiveFiltersCount()} active filters</span>
            </div>
            
            {/* Show some key filters */}
            <div className="mt-2 space-y-1 text-xs">
              {filters?.skills && filters?.skills?.length > 0 && (
                <div>Skills: {filters?.skills?.slice(0, 3)?.join(', ')}{filters?.skills?.length > 3 ? '...' : ''}</div>
              )}
              {filters?.location && (
                <div>Location: {filters?.location}</div>
              )}
              {filters?.experience && (
                <div>Experience: {filters?.experience}</div>
              )}
            </div>
          </div>

          {/* Search Name */}
          <Input
            label="Search Name"
            type="text"
            placeholder="e.g., Senior React Developers in Oslo"
            value={searchName}
            onChange={(e) => setSearchName(e?.target?.value)}
            required
          />

          {/* Email Alerts */}
          <div className="space-y-4">
            <Checkbox
              label="Email alerts for new matches"
              description="Get notified when candidates matching your criteria join the platform"
              checked={emailAlerts}
              onChange={(e) => setEmailAlerts(e?.target?.checked)}
            />

            {emailAlerts && (
              <div className="ml-6 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Alert Frequency
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'immediate', label: 'Immediate', desc: 'As soon as new matches are found' },
                      { value: 'daily', label: 'Daily', desc: 'Once per day summary' },
                      { value: 'weekly', label: 'Weekly', desc: 'Weekly summary on Mondays' }
                    ]?.map((option) => (
                      <label key={option?.value} className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="alertFrequency"
                          value={option?.value}
                          checked={alertFrequency === option?.value}
                          onChange={(e) => setAlertFrequency(e?.target?.value)}
                          className="mt-1"
                        />
                        <div>
                          <div className="text-sm font-medium text-foreground">{option?.label}</div>
                          <div className="text-xs text-muted-foreground">{option?.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Benefits */}
          <div className="bg-success/5 rounded-lg p-4 border border-success/20">
            <div className="flex items-start space-x-3">
              <Icon name="Lightbulb" size={20} className="text-success mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground text-sm">Pro Tip</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Saved searches help you stay ahead of the competition by alerting you to new talent as soon as they join the platform.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          
          <Button 
            onClick={handleSave}
            disabled={!searchName?.trim() || isSaving}
            loading={isSaving}
            iconName="Bookmark"
            iconPosition="left"
          >
            Save Search
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SavedSearchModal;